import React, { useMemo } from "react";
import { useGetSpecificFormQuery } from "../../Redux/service/Forms/Form";
import { hydrateSubmissionFields } from "../../utils/formSubmissionSchema";
import AntCheckBox from "./CheckBox";
import AntDatePicker from "./DatePicker";
import NormalInput from "./NormalInput";
import ContainerPreview from "./ContainerPreview";
import AntTextArea from "./TextArea";
import Toggle from "./Toggle";
import AntButton from "./Button";
import AntHeader from "./Header";
import Image from "./Image";
import Label from "./Label";
import Link from "./Link";
import ProgressCircle from "./ProgressCircle";
import ProgressLine from "./ProgressLine";
import Loader from "../Helpers/Loader/Loader";

/**
 * Render submitted answers in the definition's original structure.
 *
 * Form submissions only store value-bearing fields, so rendering formData directly
 * loses every Header, Label, and Container. Numeric fields submitted by the mobile
 * API are also named `numberinput`, while the web form builder calls them `Input`.
 * Hydrating by the stable field key restores that context and handles both names.
 */
export default function FormSubmissionValues({ formId, formData }) {
	const { currentData: formDefinition, isFetching } = useGetSpecificFormQuery(
		formId,
		{
			skip: !formId,
		},
	);

	const fields = useMemo(
		() =>
			hydrateSubmissionFields(
				formDefinition?.data?.formSchema?.fields,
				formData,
			),
		[formDefinition, formData],
	);

	if (formId && isFetching && !formDefinition) {
		return <Loader style={{ minHeight: "160px" }} />;
	}

	const getInputType = (item) => {
		switch (item?.name) {
			case "Checkbox":
				return <AntCheckBox item={item} readOnly={true} />;
			case "DatePicker":
				return <AntDatePicker item={item} preview={true} disable={false} />;
			case "Dropdown":
			case "Input":
			case "numberinput":
			case "Radio_group":
				return <NormalInput item={item} preview={true} readOnly={true} />;
			case "Container":
				return (
					<ContainerPreview
						getInputType={getInputType}
						ukey={item?.key}
						item={item?.container || []}
						css={item?.css}
					/>
				);
			case "Text_area":
				return <AntTextArea item={item} preview={true} readOnly={true} />;
			case "Toggle":
				return <Toggle item={item} readOnly={true} />;
			case "Uploader":
			case "Signature":
			case "Camera":
				// Submitted files are rendered once in the attachments section below the form.
				return null;
			case "Button":
				return <AntButton item={item} />;
			case "Header":
				return <AntHeader item={item} />;
			case "Image":
				return <Image item={item} />;
			case "Label":
				return <Label item={item} />;
			case "Link":
				return <Link item={item} />;
			case "Progress_circle":
				return <ProgressCircle item={item} />;
			case "Progress_line":
				return <ProgressLine item={item} />;
			default:
				return null;
		}
	};

	return fields.map((item, index) => (
		<div
			className="mb-2"
			style={item?.css}
			key={item?.key || `submission-field-${index}`}
		>
			{getInputType(item)}
		</div>
	));
}
